import { mapGetters, mapActions } from 'vuex'
import { get } from 'lodash'

const BANDWIDTH_INFINITY = -1
const BYTES_TO_GIGABYTES_FACTOR = Number(FONTAWESOME.BANDWIDTH_GIGABYTE_MULTIPLIER)

export const BandwidthUsageMixin = {
  data() {
    return {
      hasBandwidthFetched: false,
      // TODO: Metered Billing full release: remove
      isMeteredBillingBandwidthPartiallyReleased: false,
      bandwidthFetchFailed: false,
      meteredBandwidthUnits: 'GB'
    }
  },

  computed: {
    ...mapGetters('meteredBilling', ['meteredBilling']),
    ...mapGetters('tokens', ['npmToken', 'bandwidthUsed']),

    atBandwidthPlanLimit() {
      return this.totalBandwidthUsedInGigabytes >= this.planBandwidthUsageLimit
    },

    atBandwidthUsageLimit() {
      // if metered bandwidth limit is set to none (-1) then user would not be at the limit so we return false
      if (this.meteredBillingMaxMonthlyBandwidth === -1) return false

      return this.totalBandwidthUsedInGigabytes >= this.meteredBillingMaxMonthlyBandwidth
    },

    bandwidthDollarLimit() {
      return this.meteredBilling?.maxMonthlyBandwidthMoneyLimit > 0 ? this.meteredBilling?.maxMonthlyBandwidthMoneyLimit / 100 : 0
    },

    bandwidthOverage() {
      const overage = this.totalBandwidthUsed - (this.planBandwidthUsageLimit * BYTES_TO_GIGABYTES_FACTOR || 0)

      return Math.max(overage, 0)
    },

    billableBandwidthOverageBytes() {
      const planBandwidthUsageLimitBytes = this.planBandwidthUsageLimit * BYTES_TO_GIGABYTES_FACTOR

      const actualOverageBytes = Math.max(this.totalBandwidthUsed - planBandwidthUsageLimitBytes, 0)

      const overageBandwidthIncrementGigabytes = this.$subscription?.plan?.overageBandwidthIncrement || 0

      const isOverageLessThanOneIncrement = actualOverageBytes < overageBandwidthIncrementGigabytes * BYTES_TO_GIGABYTES_FACTOR

      if (isOverageLessThanOneIncrement) {
        // We don't bill for these small overages.
        return 0
      }

      if (this.isMeteredBillingBandwidthCustomerEnabled && this.hasInfiniteBandwidth) {
        // If bandwidth is metered and unlimited, then overage billing is based
        // simply on the amount of bytes over the plan allownace, with no capping
        // to a limit, since there is no limit -- unless it's merely a toe over the line.
        return actualOverageBytes
      }

      const maxMonthlyBandwidthBytes = this.meteredBillingMaxMonthlyBandwidth * BYTES_TO_GIGABYTES_FACTOR

      const isMeteredBillingLimitedWithExcess = this.isMeteredBillingBandwidthCustomerEnabled && this.totalBandwidthUsed >= maxMonthlyBandwidthBytes

      if (isMeteredBillingLimitedWithExcess) {
        // cap billable overages at the max monthly, which is the limit set by the customer
        return Math.max(maxMonthlyBandwidthBytes - planBandwidthUsageLimitBytes, 0)
      }

      return actualOverageBytes
    },

    bandwidthOverageToGigabytes() {
      return this.bandwidthOverage / BYTES_TO_GIGABYTES_FACTOR
    },

    bandwidthUsageOverageLimit() {
      return this.meteredBillingMaxMonthlyBandwidth - this.planBandwidthUsageLimit
    },

    bandwidthUsageOveragePercentage() {
      const overage = this.bandwidthOverageToGigabytes > 0 ? this.bandwidthOverageToGigabytes : 0
      const percentage = (overage / this.bandwidthUsageOverageLimit) * 100

      return percentage
    },

    bandwidthOverageDangerPercentage() {
      return this.bandwidthUsageOverageLimit ? 100 : this.planBandwidthUsageLimit * 10
    },

    hasSetBandwidthBillingLimit() {
      return this.$subscription?.meteredBilling.maxMonthlyBandwidthMoneyLimit > 0
    },

    hasInfiniteBandwidth() {
      return this.meteredBillingMaxMonthlyBandwidth === BANDWIDTH_INFINITY
    },

    isMeteredBillingBandwidthFeatureOn() {
      return FONTAWESOME.METERED_BANDWIDTH_ACTIVE === 'yes'
    },

    isMeteredBillingBandwidthCustomerEnabled() {
      return this.meteredBilling?.meteredBandwidthOn
    },

    isMeteredBillingBandwidth() {
      return this.isMeteredBillingBandwidthFeatureOn && this.isMeteredBillingBandwidthCustomerEnabled
    },

    meteredBillingMaxMonthlyBandwidth() {
      return get(this.meteredBilling, ['maxMonthlyBandwidth'], -1)
    },

    planBandwidthOverageUnitPrice() {
      return this.$subscription?.plan?.overageBandwidthPrice?.amount
    },

    planBandwidthUsageLimit() {
      return get(this.$subscription, ['plan', 'monthlyBandwidth'], 0)
    },

    planBandwidthUsagePercentage() {
      return (this.totalBandwidthUsed / BYTES_TO_GIGABYTES_FACTOR / this.planBandwidthUsageLimit) * 100
    },

    planHasAccessToBandwidthMeteredBilling() {
      return this.$subscription?.plan?.meteredBillingBandwidthEnabled
    },

    totalBandwidthUsed() {
      const bandwidthObject = this.bandwidthUsed
      const bandwidth = get(bandwidthObject, ['bandwidth'], 0)

      return bandwidth
    },

    totalBandwidthUsedInGigabytes() {
      return this.totalBandwidthUsed / BYTES_TO_GIGABYTES_FACTOR
    },

    totalBandwidthOverageCost() {
      if (!this.$subscription) {
        // If the $subscription shortcut has not yet been resolved, don't
        // divide by zero--return early. This will re-run when this.$subscription
        // is ready.
        return 0
      }

      if (!this.$subscription.plan.privateRegistriesEnabled) {
        // When the plan doesn't support bandwidth.
        return 0
      }

      const pricePerOverageIncrementCents = this.$subscription.plan.overageBandwidthPrice.amount || 0
      const overageBandwidthIncrement = this.$subscription.plan.overageBandwidthIncrement
      if (overageBandwidthIncrement <= 0) {
        // Again, let's return 0 instead of possibly dividing by a non-sense number, just in case.
        return 0
      }

      const bandwidthOverageGigabytes = this.billableBandwidthOverageBytes / BYTES_TO_GIGABYTES_FACTOR

      const additionalIncrements = bandwidthOverageGigabytes / overageBandwidthIncrement

      // We don't bill them until they've used at least one whole increment of usage
      return additionalIncrements >= 1 ? pricePerOverageIncrementCents * additionalIncrements : 0
    },

    npmTokenDeactivated() {
      if (this.npmToken) return this.npmToken.active !== true

      return false
    }
  },

  async created() {
    if (this.$subscription) {
      try {
        if (this.$subscription?.meteredBilling?.id) {
          await this.fetchMeteredBilling()
        }

        await this.fetchTokenUsage()

        this.hasBandwidthFetched = true
      } catch (e) {
        this.bandwidthFetchFailed = true
      }
    }
  },

  methods: {
    ...mapActions('meteredBilling', ['fetchMeteredBilling']),

    ...mapActions('tokens', ['fetchTokenUsage'])
  }
}
